/**
 * The student shell: the frame around every student view on a phone.
 *
 *   ┌ head: my name (leaf chip) · my points ┐
 *   │ body: lobby, the module's student view, │
 *   │       or "Schau auf die Tafel"          │
 *   └─────────────────────────────────────────┘
 *
 * - Points come from the module's student view, key `shell`
 *   (`StudentShellInfo`), so the server stays the authority.
 * - A student view of `null`, or a format without a student view, shows
 *   "Schau auf die Tafel".
 * - Media are never loaded here: the view context the shell hands the
 *   module has a `mediaUrl` that gives nothing and a `preloadMedia` that
 *   does nothing (rule 2 in game-client.ts, ADR 0002).
 * - While a game runs the screen stays on (Screen Wake Lock, where the
 *   browser has it), so a phone does not lock itself mid-question.
 */
import type { StudentShellInfo } from '@schulspiele/protocol';

import { esc, qs } from './dom';
import { confirmDialog } from './ui/dialog';
import type { EntryPoint } from './game-client';
import { t } from './i18n/de';
import { mountGameView } from './view-host';
import type { SessionClient } from './ws-client';
import './student-shell.css';

export interface StudentShell {
  setName(name: string): void;
  /** `onLeave`: a small "wrong game?" button that gives up the seat. */
  showLobby(gameName: string, onLeave?: () => void): void;
  /** Outside the lobby: a small "Anderes Spiel?" in the head (asks first); null hides it. */
  setLeave(onLeave: (() => void) | null): void;
  /** The game runs: the module's student view, or the look-at-board hint. */
  showGame(entry: EntryPoint | undefined, participantId: string): void;
  showEnded(): void;
  /** A full-screen message instead of everything else (kicked, errors). */
  showMessage(html: string): void;
  destroy(): void;
}

const LEAF = `<svg class="shell-leaf" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5C19.5 6 21 15 12 21.5 3 15 4.5 6 12 2.5Z" fill="var(--forest-leaf)" stroke="var(--forest-deep)" stroke-width="1.4" stroke-linejoin="round"/><path d="M12 6v13" stroke="var(--forest-deep)" stroke-width="1.1"/></svg>`;

export function createStudentShell(
  mount: HTMLElement,
  client: SessionClient,
  art: { sapling: string; owl: string },
): StudentShell {
  mount.innerHTML = `
    <div class="shell">
      <header class="shell__head" hidden data-head>
        <span class="shell__me">${LEAF}<span class="shell__name" data-name></span></span>
        <span class="shell__score" data-score hidden></span>
        <button type="button" class="shell__exit" data-exit hidden>${t('join.otherGameShort')}</button>
      </header>
      <main class="shell__body" data-body></main>
    </div>`;
  const head = qs(mount, '[data-head]');
  const body = qs(mount, '[data-body]');
  const scoreEl = qs(mount, '[data-score]');
  const exitBtn = qs<HTMLButtonElement>(mount, '[data-exit]');
  let leaveFn: (() => void) | null = null;
  exitBtn.addEventListener('click', () => {
    const go = leaveFn;
    if (go === null) return;
    // In a running game a stray tap must not cost the seat.
    void confirmDialog(t('join.leaveConfirm'), {
      confirmLabel: t('join.leaveButton'),
      danger: true,
    }).then((ok) => {
      if (ok) go();
    });
  });
  let unmount: (() => void) | null = null;
  let hasModuleView = false;
  let wakeLock: WakeLockSentinel | null = null;
  let wantWake = false;

  const setScore = (info: StudentShellInfo | undefined): void => {
    const score = info?.score;
    scoreEl.hidden = score === undefined;
    if (score === undefined) return;
    // "1.250 Punkte", "−40 Punkte": the same figures as in the games' own screens.
    const shown = Math.abs(score).toLocaleString('de-DE');
    const text = `${score < 0 ? '−' : ''}${shown} ${Math.abs(score) === 1 ? 'Punkt' : 'Punkte'}`;
    if (scoreEl.textContent !== text) {
      const grew = scoreEl.textContent !== '';
      scoreEl.textContent = text;
      if (grew) {
        scoreEl.classList.remove('is-bumped');
        scoreEl.getBoundingClientRect();
        scoreEl.classList.add('is-bumped');
      }
    }
  };

  const lookAtBoard = (): string => `
    <div class="shell__look">
      <div class="shell__owl" aria-hidden="true">${art.owl}</div>
      <h1>${t('join.lookAtBoard')}</h1>
    </div>`;

  /** Module view or the hint, depending on whether the server sent anything. */
  const onView = (view: unknown): void => {
    const v = view as ({ shell?: StudentShellInfo } & Record<string, unknown>) | null;
    setScore(v?.shell);
    body.classList.toggle('is-looking', v === null || !hasModuleView);
    const hint = body.querySelector<HTMLElement>('[data-look]');
    if (hint !== null) hint.hidden = v !== null && hasModuleView;
    const game = body.querySelector<HTMLElement>('[data-game]');
    if (game !== null) game.hidden = v === null;
  };
  const offView = client.on('view', onView);

  const acquireWake = async (): Promise<void> => {
    // Older Safari has no Wake Lock API; the phone may lock, reconnect handles it.
    if (!wantWake || wakeLock !== null || !('wakeLock' in navigator)) return;
    try {
      wakeLock = await navigator.wakeLock.request('screen');
    } catch {
      // Battery saver or no permission: the phone may lock, reconnect handles it.
    }
  };
  const releaseWake = (): void => {
    wantWake = false;
    void wakeLock?.release().catch(() => undefined);
    wakeLock = null;
  };
  // The browser drops the wake lock when the page is hidden; take it again on return.
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') {
      wakeLock = null;
      void acquireWake();
    }
  };
  document.addEventListener('visibilitychange', onVisible);

  const clearGame = (): void => {
    unmount?.();
    unmount = null;
  };

  return {
    setName(n) {
      const nameEl = qs(mount, '[data-name]');
      nameEl.textContent = n;
      // The whole name on a long press or for a screen reader when it ends in "…".
      nameEl.title = n;
      head.hidden = n === '';
    },
    setLeave(onLeave) {
      leaveFn = onLeave;
      exitBtn.hidden = onLeave === null || body.classList.contains('is-lobby');
    },
    showLobby(gameName, onLeave) {
      exitBtn.hidden = true;
      clearGame();
      releaseWake();
      body.className = 'shell__body is-lobby';
      body.innerHTML = `
        <div class="shell__lobby">
          <div class="shell__sapling" aria-hidden="true">${art.sapling}</div>
          <h1>${esc(gameName)}</h1>
          <p class="lead">${t('join.waiting')}</p>
          ${onLeave === undefined ? '' : `<button type="button" class="btn btn--ghost btn--small shell__leave" data-leave>${t('join.otherGame')}</button>`}
        </div>`;
      body.querySelector('[data-leave]')?.addEventListener('click', () => {
        onLeave?.();
      });
    },
    showGame(entry, participantId) {
      clearGame();
      wantWake = true;
      void acquireWake();
      hasModuleView = entry !== undefined;
      body.className = 'shell__body is-game';
      exitBtn.hidden = leaveFn === null;
      body.innerHTML = `<div data-look hidden>${lookAtBoard()}</div><div class="shell__game" data-game></div>`;
      if (entry !== undefined) {
        unmount = mountGameView(entry, qs(body, '[data-game]'), client, 'student', participantId);
      }
      onView(client.lastView);
    },
    showEnded() {
      clearGame();
      releaseWake();
      body.className = 'shell__body is-ended';
      exitBtn.hidden = true;
      body.innerHTML = `
        <div class="shell__lobby">
          <h1>${t('join.ended')}</h1>
          <a class="btn btn--big" href="/spielen">${t('join.again')}</a>
        </div>`;
    },
    showMessage(html) {
      clearGame();
      releaseWake();
      body.className = 'shell__body is-message';
      exitBtn.hidden = true;
      body.innerHTML = `<div class="shell__lobby">${html}</div>`;
    },
    destroy() {
      offView();
      clearGame();
      releaseWake();
      document.removeEventListener('visibilitychange', onVisible);
      mount.innerHTML = '';
    },
  };
}
